import { ReactNode } from 'react';
import { ColumnsType, ColumnType } from 'antd/lib/table';
import { mainDateFormat } from './main-date-format';

export interface ReportExportMetric {
  caption?: string;
  label: string;
  value: ReactNode;
}

export interface ReportExportTableData {
  header: string[];
  rows: (string | number)[][];
}

export interface ReportExportScope {
  asOfDate?: string;
  timeZone?: string;
  page: number;
  size: number;
  total: number;
  filters: object;
}

export type ReportExportColumn<T> = ColumnType<T> & {
  exportValue?: (record: T) => string | number;
};

export const getReportExportScopeText = ({
  page,
  size,
  total,
  filters,
  asOfDate,
  timeZone,
}: ReportExportScope): string =>
  `${
    asOfDate
      ? `Calculated as of ${mainDateFormat(asOfDate)} (${timeZone}). `
      : ''
  }Table: current page ${page}, page size ${size}, ${total} filtered rows. ${
    asOfDate ? '' : 'KPIs/charts: full filtered dataset. '
  }Filters: ${JSON.stringify(filters, (key: string, value: unknown) =>
    /(?:date|period)(?:from|to)?$/i.test(key) && typeof value === 'string'
      ? mainDateFormat(value)
      : value,
  )}`;

export const toReportExportCellValue = (value: unknown): string | number => {
  if (typeof value === 'string' || typeof value === 'number') {
    return value;
  }

  if (value === undefined || value === null) return '';

  if (typeof value === 'boolean') return value ? 'Yes' : 'No';

  if (Array.isArray(value))
    return value.map(toReportExportCellValue).join(', ');

  throw new Error('A report export column requires a plain text formatter.');
};

const getColumnDataIndexValue = <T>(
  record: T,
  dataIndex: ColumnType<T>['dataIndex'],
): unknown => {
  if (Array.isArray(dataIndex)) {
    return dataIndex.reduce<unknown>(
      (value: unknown, key: string | number) =>
        (value as Record<string | number, unknown> | undefined)?.[key],
      record,
    );
  }

  return dataIndex === undefined
    ? undefined
    : (record as Record<string | number, unknown>)?.[
        dataIndex as string | number
      ];
};

const getColumnCellValue = <T>(
  column: ReportExportColumn<T>,
  record: T,
  index: number,
): string | number => {
  if (column.exportValue) return column.exportValue(record);

  const rawValue = getColumnDataIndexValue<T>(record, column.dataIndex);
  const renderedValue = column.render
    ? column.render(rawValue, record, index)
    : rawValue;

  return typeof renderedValue === 'string' || typeof renderedValue === 'number'
    ? renderedValue
    : toReportExportCellValue(rawValue);
};

export const getReportExportTableData = <T>(
  columns: ColumnsType<T>,
  rows: T[],
): ReportExportTableData => ({
  header: columns.map((column: ColumnType<T>) => column.title as string),
  rows: rows.map((record: T, index: number) =>
    columns.map((column: ColumnType<T>) =>
      getColumnCellValue<T>(column, record, index),
    ),
  ),
});
